/*
 * WHERE A NEW BUBBLE STARTS, READ ONCE FOR A WHOLE BATCH (2026-10-19). A visitor's bubble appears at its category's row in the Real Time list, so drawing one asks the page where that row's
 * dot is (getBoundingClientRect) and where the canvas is (getScreenCTM). Both are layout reads, and the bubble just drawn - and the ones before it - changed the page, so each read made the
 * browser lay the whole page out AGAIN, once per bubble. In a Playback at 3x, where dozens of visitors arrive in a frame, a CPU profile of the real page had that one call, in
 * getSidebarOriginCoords, at 17% of ALL the time the page spent running (2.5 s of 14.7 s) - the "frozen, dark screen" Chris met on coming back from Playback at 3x.
 *
 * The list does not move between two bubbles of one batch, so ONE pass reads every row's dot and the canvas's transform together (one layout, however many rows), and every bubble drawn in the
 * next `maxAgeMs` is answered from that pass. A name the pass did not have (a row that has only just appeared) gets ONE fresh pass, unless the pass was only just made (then it is simply not
 * there: asking again could not find it either). Pure (the readers and the clock are passed in) so it can be tested without a browser.
 */
export interface Centre {
  x: number;
  y: number;
}

export interface OriginPass<T> {
  at: number;
  /** each row's dot centre in screen pixels, or null where the dot has no size (not rendered / clipped away) */
  centres: Map<string, Centre | null>;
  /** whatever else the reader needs from the same moment (the canvas's transform) */
  extra: T;
}

export interface OriginReader<T> {
  /** the pass to answer `name` from, or null if there is none to be had */
  lookup: (name: string) => { centre: Centre | null; extra: T } | null;
  /** forget the current pass (the canvas was rebuilt, the list replaced) */
  reset: () => void;
}

export const createOriginReader = <T>(options: {
  now: () => number;
  /** one pass over every row: null when the page has nothing to read from (no canvas yet) */
  read: () => { centres: Map<string, Centre | null>; extra: T } | null;
  /** how long a pass is answered from (a few frames) */
  maxAgeMs: number;
  /** a pass younger than this is not repeated for a missing name */
  freshMs?: number;
}): OriginReader<T> => {
  const freshMs = options.freshMs ?? 16;
  let pass: OriginPass<T> | null = null;

  const take = (): OriginPass<T> | null => {
    const read = options.read();
    pass = read ? { at: options.now(), centres: read.centres, extra: read.extra } : null;

    return pass;
  };

  return {
    lookup: (name) => {
      const now = options.now();
      let current = pass && now - pass.at < options.maxAgeMs ? pass : take();
      if (!current) return null;
      if (!current.centres.has(name) && now - current.at >= freshMs) current = take();
      if (!current) return null;

      return { centre: current.centres.get(name) ?? null, extra: current.extra };
    },
    reset: () => {
      pass = null;
    },
  };
};
